import * as axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { GetCustomerDetailsResult, PaywallApiResponse, PaywallAuthErrorCodes, RevalidateUserData, SignInType } from "./paywallInterfaces";
import { getPersonalCustomerDetails, getSendRevalidateLogin, getValidateOtp, updateMyaccountCustomerDetails, updatePersonalCustomerDetails } from "./paywallApiUrls";

// Local AxiosError-like typing and guard for axios 0.15.3
type AxiosErrorLike<T = any> = {
    isAxiosError?: boolean;
    message?: string;
    name?: string;
    code?: string;
    config?: any;
    request?: any;
    response?: {
        data?: T;
        status?: number;
        statusText?: string;
        headers?: any;
        config?: any;
    };
};
const isAxiosError = <T = any>(err: unknown): err is AxiosErrorLike<T> => {
    return typeof err === "object" && err !== null &&
        ("isAxiosError" in err || "response" in err || "config" in err);
};

const PAYWALL_LOCAL_STORAGE_KEY = 'paywall'

export enum DetailsUpdateMode {
    PURCHASE = 1,
    MY_ACCOUNT = 2,
}

export interface CustomerDetails {
    email: string;
    firstName: string | null;
    lastName: string | null;
    personalId: string | null;
    phoneNumber: string | null;
}

export enum DetailUpdateError {
    NO_ERROR,
    UNKNOWN_ERROR,
    NOT_LOGGED_IN,
    INVALID_DETAILS,
    TERMS_NOT_ACCEPTED,
    AUTHENTICATION_FAILED,
    OTP_MAX_ATTEMPTS,
    EXISTING_PERSONAL_ID,
}

export enum RevalidationStep {
    SEND,
    ENTER_OTP,
}

const getDefaultCustomerDetails = (): CustomerDetails => ({
    email: "",
    firstName: null,
    lastName: null,
    personalId: null,
    phoneNumber: null,
});

export interface UseDetailsUpdateReturn {
    customerDetails: CustomerDetails
    setCustomerDetails: (value: CustomerDetails) => void
    setStateCustomerDetails: (value: CustomerDetails) => void
    agreeToTermsOfUse: boolean
    setAgreeToTermsOfUse: (value: boolean) => void
    revalidationType: SignInType
    setRevalidationType: (type: SignInType) => void
    error: DetailUpdateError
    clearError: () => void
    revalidateUser: RevalidateUserData | null
    saved: boolean
    revalidationStep: RevalidationStep
    saveCustomerDetails: () => Promise<boolean>
    sendRevalidation: () => Promise<void>
    validateOtp: (otp: string) => Promise<boolean>
}

export const useDetailsUpdate = (mode: DetailsUpdateMode): UseDetailsUpdateReturn => {
    const [error, setError] = useState<DetailUpdateError>(DetailUpdateError.NO_ERROR);
    const [customerDetails, setCustomerDetails] = useState<CustomerDetails>(getDefaultCustomerDetails());
    const [revalidateUser, setRevalidateUser] = useState<RevalidateUserData | null>(null);
    const [revalidationType, setStateRevalidationType] = useState<SignInType>(SignInType.otpEmail);
    const [agreeToTermsOfUse, setAgreeToTermsOfUse] = useState<boolean>(false);
    const [saved, setSaved] = useState<boolean>(false);
    const [revalidationStep, setRevalidationStep] = useState<RevalidationStep>(RevalidationStep.SEND);



    const setStateCustomerDetails = (value: CustomerDetails) => {
        setCustomerDetails(value);
        setSaved(false); 
    };

    const clearError = () => {
        setError(DetailUpdateError.NO_ERROR);
    };

    const getCustomerDetailsFromServer = async (): Promise<GetCustomerDetailsResult | undefined> => {
        try {
            const response = await axios.get<PaywallApiResponse<GetCustomerDetailsResult>>(getPersonalCustomerDetails());
            return response.data.data;
        } catch (error) {
            let consoleError = [error]
            if (isAxiosError<PaywallApiResponse<GetCustomerDetailsResult>>(error)) {
                if (error.response && error.response.data && error.response.data.error) {
                    consoleError = [error.response.data.error.message, error.response.data.error.errorCode]
                }
            }
            console.error(...consoleError);
            setError(DetailUpdateError.UNKNOWN_ERROR);
        }
    };

    const loadCustomerDetails = useCallback(async () => {
        const paywallData = typeof window !== "undefined" && window && window.YitPaywall && typeof window.YitPaywall.getPaywallDataObj === "function" && window.YitPaywall.getPaywallDataObj();
        if (!paywallData || !paywallData.id) {
            setError(DetailUpdateError.NOT_LOGGED_IN);
        }
        const customerDetails = await getCustomerDetailsFromServer();
        if (!customerDetails) return;
        if (customerDetails.revalidateUser) {
            setRevalidationStep(RevalidationStep.SEND);
            setRevalidateUser(customerDetails.revalidateUser);
        } else {
            setCustomerDetails({
                email: customerDetails.email,
                firstName: customerDetails.firstName,
                lastName: customerDetails.lastName,
                personalId: customerDetails.personalId,
                phoneNumber: customerDetails.phoneNumber,
            });
            setRevalidateUser(null);
        }
    }, []);

    const saveCustomerDetails = async () => {
        setError(DetailUpdateError.NO_ERROR);
        const url = mode === DetailsUpdateMode.PURCHASE ? updatePersonalCustomerDetails() : updateMyaccountCustomerDetails();
        if (mode === DetailsUpdateMode.PURCHASE && !agreeToTermsOfUse) {
            setError(DetailUpdateError.TERMS_NOT_ACCEPTED);
            return false;
        }

        const payload = {
            details: {
                ...customerDetails,
                signUpAgreement: mode === DetailsUpdateMode.PURCHASE ? agreeToTermsOfUse : undefined
            }
        };

        try {
            await axios.post<PaywallApiResponse<void>>(url, payload);
            setRevalidateUser(null);
            setSaved(true);
            return true;
        } catch (error) {
            let consoleError = [error]
            if (isAxiosError<PaywallApiResponse<void>>(error)) {
                if (error.response && error.response.data && error.response.data.error) {
                    if (error.response.data.error.errorCode === PaywallAuthErrorCodes.UPDATE_BLOCKED) {
                        setRevalidationStep(RevalidationStep.SEND);
                        setRevalidateUser(error.response.data.error.parms as RevalidateUserData);
                        return false;
                    }
                    if (error.response.data.error.errorCode === PaywallAuthErrorCodes.EXISTING_PERSONAL_ID) {
                        setError(DetailUpdateError.EXISTING_PERSONAL_ID);
                        return false;
                    } else {
                        consoleError = [error.response.data.error.message, error.response.data.error.errorCode]
                    }
                }
            }
            console.error(...consoleError);
            setError(DetailUpdateError.UNKNOWN_ERROR);
            return false;
        }
    };

    const sendRevalidation = async () => {
        try {
            setError(DetailUpdateError.NO_ERROR);
            await axios.get(getSendRevalidateLogin(revalidationType));
            setRevalidationStep(RevalidationStep.ENTER_OTP);
        } catch(error){
            if (isAxiosError<PaywallApiResponse<void>>(error)) {
                if (error.response && error.response.data && error.response.data.error) {
                    if (error.response.data.error.errorCode === PaywallAuthErrorCodes.OTP_MAX_ATTEMPTS) {
                        setError(DetailUpdateError.OTP_MAX_ATTEMPTS);
                        return;
                    }
                }
            }
            setError(DetailUpdateError.UNKNOWN_ERROR);
        }
    }

    const validateOtp = async (otp: string) => {
        try {
            setError(DetailUpdateError.NO_ERROR);
            const response = await axios.get<PaywallApiResponse<GetCustomerDetailsResult>>(getValidateOtp(otp));
            const customerDetails = response.data.data;
            setRevalidateUser(null);
            setCustomerDetails({
                email: customerDetails.email,
                firstName: customerDetails.firstName,
                lastName: customerDetails.lastName,
                personalId: customerDetails.personalId,
                phoneNumber: customerDetails.phoneNumber,
            });
            return true;
        } catch (error) {
            if (isAxiosError<PaywallApiResponse<void>>(error)) {
                if (error.response && error.response.data && error.response.data.error) {
                    if (error.response.data.error.errorCode === PaywallAuthErrorCodes.INVALID_PASSWORD) {
                        setError(DetailUpdateError.AUTHENTICATION_FAILED);
                        return;
                    }
                }
            }
            setError(DetailUpdateError.UNKNOWN_ERROR);
            return false;
        }
    };

    const setRevalidationType = (type: SignInType) => {
        if (revalidateUser && revalidateUser.obfuscatedPhoneNumber) {
            //only if the customer has phone number we can change from the default eMail.
            setStateRevalidationType(type);
        }
    };

    useEffect(() => {
        loadCustomerDetails();
    }, [loadCustomerDetails]);

    return {
        customerDetails, setCustomerDetails, setStateCustomerDetails,
        agreeToTermsOfUse, setAgreeToTermsOfUse,
        revalidationType, setRevalidationType,
        error, clearError,
        revalidateUser,
        saved,
        revalidationStep,
        saveCustomerDetails,
        sendRevalidation,
        validateOtp,
    };
}